import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import { FormField } from '@/App/tiles/ForwardAccumulator/parts/RightPanel/common/FormField.tsx';
import {
  useCheckForwardAccumulatorByTradeCapture,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput/index.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import {
  selectForwardAccumulatorCurrencyData,
  selectForwardAccumulatorProperty,
  selectForwardAccumulatorValidation,
} from '@/store/forwardAccumulator/selectors.ts';
import { forwardAccumulatorSlice } from '@/store/forwardAccumulator/slice.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';

const Notional = () => {
  const tileId = useForwardAccumulatorTileId();
  const check = useCheckForwardAccumulatorByTradeCapture();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const dispatch = useAppDispatch();

  const { currency, notionalCurrencyIndex } = useAppSelector((state) =>
    selectForwardAccumulatorCurrencyData(state, tileId),
  );
  const notional = useAppSelector((state) => selectForwardAccumulatorProperty(state, tileId, 'notional'));
  const validation = useAppSelector((state) => selectForwardAccumulatorValidation(state, tileId));

  const handleNotionalChanged = (value: number | undefined) => {
    sgwtWebAnalytics?.trackEvent('Forward Accumulator', 'Notional changed', value?.toString() ?? 'null');

    return check({
      notional: isDefined(value) ? value : null,
    });
  };

  const handleNotionalCurrencyClick = () => {
    const currencyIndex = notionalCurrencyIndex === 1 ? 2 : 1;

    dispatch(forwardAccumulatorSlice.actions.notionalCurrencyChanged({ tileId, notionalCurrencyIndex: currencyIndex }));

    return check({
      notionalCurrencyIndex: currencyIndex,
    });
  };

  return (
    <FormField formattedMessageId="forwardAccumulator.form.notional">
      <TradeCaptureValidationWrapper validation={validation?.notional}>
        <SgAmountInput
          value={notional === null ? undefined : notional}
          onChange={handleNotionalChanged}
          unit={currency}
          allowNegative={false}
          unitBackgroundColor={notionalCurrencyIndex === 2 ? 'warning' : undefined}
          onUnitClick={handleNotionalCurrencyClick}
          size="sm"
          selectAllOnFocus
          data-e2e="notional"
        />
      </TradeCaptureValidationWrapper>
    </FormField>
  );
};

export default Notional;
